import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight, MessageCircle, Sparkles } from "lucide-react";
import { IMAGES, WHATSAPP_NUMBER } from "../data/menu";
import { scrollToId } from "../lib/scroll";
import { BotanicalOrbit, Leaf, Petal } from "./Decor";

const SLIDES = [
  {
    lines: ["Homemade Food,", "Made with Love"],
    sub: "Fresh, hygienic and homemade — from our kitchen straight to your table.",
  },
  {
    lines: ["Taste, Tradition", "and Celebration"],
    sub: "Traditional recipes cooked on order, the way home food should be.",
  },
  {
    lines: ["Your Favourite Dishes,", "Just a Few Clicks Away"],
    sub: "Browse the menu, fill your cart and place your order on WhatsApp.",
  },
];

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.7, delay: 0.12 * i, ease: [0.22, 1, 0.36, 1] } }),
  exit: { y: "-115%", transition: { duration: 0.45, ease: [0.55, 0, 0.55, 1] } },
};

export const Hero = () => {
  const [index, setIndex] = useState(0);
  const timer = useRef(null);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const go = useCallback((dir) => setIndex((p) => (p + dir + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    timer.current = setInterval(() => go(1), 5500);
    return () => clearInterval(timer.current);
  }, [go, index]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-[#FFFDF8]" data-testid="hero-section">
      <BotanicalOrbit className="absolute -right-24 -top-24 w-[420px] h-[420px] sm:w-[560px] sm:h-[560px] pointer-events-none" />
      <Petal className="absolute left-[6%] top-[18%] w-4 h-4" delay={0.4} />
      <Petal className="absolute left-[42%] top-[10%] w-3 h-3 hidden sm:block" delay={1.6} />
      <Petal className="absolute left-[16%] bottom-[12%] w-3.5 h-3.5 hidden sm:block" delay={2.4} />
      <Leaf className="absolute left-0 bottom-8 w-12 h-12 sm:w-16 sm:h-16 pointer-events-none" />
      <Leaf flip className="absolute right-2 top-24 w-10 h-10 sm:w-14 sm:h-14 pointer-events-none hidden sm:block" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 sm:pt-20 sm:pb-28 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center relative">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-[#EBF2ED] text-[#3A6B4C] px-4 py-1.5 text-xs font-semibold tracking-widest uppercase mb-6"
            data-testid="hero-badge"
          >
            <Sparkles size={13} /> Fresh • Hygienic • Homemade — Est. 2025
          </motion.div>

          <div className="min-h-[190px] sm:min-h-[230px] lg:min-h-[260px]">
            <AnimatePresence mode="wait">
              <motion.h1
                key={index}
                className="font-display font-bold text-[#3D2314] text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                data-testid="hero-headline"
              >
                {SLIDES[index].lines.map((line, i) => (
                  <span key={line} className="block overflow-hidden pb-1">
                    <motion.span
                      className={`block ${i === SLIDES[index].lines.length - 1 ? "text-[#D86A7A] italic" : ""}`}
                      custom={i}
                      variants={lineReveal}
                      initial="hidden"
                      animate="show"
                      exit="exit"
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
              </motion.h1>
            </AnimatePresence>
            <AnimatePresence mode="wait">
              <motion.p
                key={`sub-${index}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.35 } }}
                exit={{ opacity: 0, transition: { duration: 0.25 } }}
                className="mt-5 text-base sm:text-lg text-[#5A4232] max-w-md leading-relaxed"
                data-testid="hero-subtitle"
              >
                {SLIDES[index].sub}
              </motion.p>
            </AnimatePresence>
          </div>

          <p className="font-hand text-2xl text-[#3A6B4C] mt-2">Taste, Tradition and Celebration with Love</p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-wrap items-center gap-4 mt-7"
          >
            <button
              data-testid="hero-order-now-btn"
              onClick={() => scrollToId("menu")}
              className="rounded-full bg-[#D86A7A] hover:bg-[#C85265] text-white px-8 py-3.5 text-sm sm:text-base font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-[0_14px_30px_-8px_rgba(216,106,122,0.55)]"
            >
              Order Now
            </button>
            <a
              data-testid="hero-whatsapp-btn"
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hello Harshi's Kitchen! I'd like to place an order.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#FAF4EC] border border-[#E8D8C8] hover:border-[#3A6B4C] text-[#3D2314] px-7 py-3.5 text-sm sm:text-base font-semibold transition-all duration-300 hover:-translate-y-0.5"
            >
              <MessageCircle size={17} className="text-[#3A6B4C]" /> WhatsApp Us
            </a>
          </motion.div>

          <div className="flex items-center gap-3 mt-8">
            <button
              data-testid="hero-slide-prev"
              onClick={() => go(-1)}
              aria-label="Previous slide"
              className="w-10 h-10 rounded-full border border-[#E8D8C8] bg-white flex items-center justify-center text-[#3D2314] hover:bg-[#FAF4EC] transition-colors duration-300"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="hero-slide-next"
              onClick={() => go(1)}
              aria-label="Next slide"
              className="w-10 h-10 rounded-full border border-[#E8D8C8] bg-white flex items-center justify-center text-[#3D2314] hover:bg-[#FAF4EC] transition-colors duration-300"
            >
              <ChevronRight size={18} />
            </button>
            <div className="flex gap-2 ml-2">
              {SLIDES.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === index ? "w-7 bg-[#D86A7A]" : "w-1.5 bg-[#E8D8C8]"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <motion.div style={{ y: imgY }} className="relative" data-testid="hero-image-wrap">
          <div className="absolute -inset-6 rounded-[46%_54%_52%_48%/48%_46%_54%_52%] bg-[#FAF4EC] -z-10" />
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden rounded-[42%_58%_55%_45%/48%_44%_56%_52%] shadow-[0_30px_60px_-20px_rgba(61,35,20,0.35)] aspect-[4/3.4]"
          >
            <motion.img
              src={IMAGES.hero}
              alt="Homemade Indian food spread at Harshi's Kitchen"
              style={{ scale: imgScale }}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="absolute -bottom-5 left-6 bg-white rounded-2xl shadow-[0_16px_40px_-12px_rgba(61,35,20,0.25)] px-5 py-3.5 flex items-center gap-3"
          >
            <span className="w-2.5 h-2.5 rounded-full bg-[#3A6B4C] animate-pulse" />
            <div className="text-xs">
              <p className="font-bold text-[#3D2314] text-sm">Open Every Day</p>
              <p className="text-[#8A7364]">9:00 AM – 6:00 PM</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
